"use client";

import { useEffect, useState } from "react";

interface Forecast {
  id: string;
  signKey: string;
  signName: string;
  signSymbol?: string;
  title: string;
  bodyHtml: string;
  forecastDate: string;
}

interface ForecastResponse {
  success: boolean;
  data: Forecast[];
}

export function ForecastBlock() {
  const [forecasts, setForecasts] = useState<Forecast[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchForecasts() {
      try {
        const res = await fetch("/api/forecasts/today");
        if (!res.ok) throw new Error("Failed to fetch forecasts");
        const json: ForecastResponse = await res.json();
        if (json.success && json.data) {
          setForecasts(json.data);
        } else {
          setError("No forecasts available");
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load forecasts");
      } finally {
        setLoading(false);
      }
    }
    fetchForecasts();
  }, []);

  if (loading) {
    return (
      <div className="forecast-shell mx-auto max-w-6xl px-4 py-12">
        <div className="text-center text-[var(--color-text)]">Loading today&apos;s forecasts...</div>
      </div>
    );
  }

  if (error || forecasts.length === 0) {
    return (
      <div className="forecast-shell mx-auto max-w-6xl px-4 py-12">
        <div className="text-center text-[var(--color-text)]">
          <h2 className="text-3xl font-bold mb-4">Daily Horoscopes</h2>
          <p className="text-[var(--color-secondary)]">Check back soon for today&apos;s readings!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="forecast-shell mx-auto max-w-6xl px-4 py-12" id="today-forecasts">
      <div className="text-center mb-8">
        <h2 className="text-4xl font-bold text-[var(--color-text)] mb-2">
          Today&apos;s Horoscopes
        </h2>
        <p className="text-[var(--color-secondary)]">
          {new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {forecasts.map((forecast) => (
          <article
            key={forecast.id}
            className="forecast-sign-tile p-6 border border-[var(--color-border)] rounded-2xl bg-[var(--color-surface)]"
          >
            <div className="flex items-center gap-3 mb-4">
              {forecast.signSymbol && (
                <span className="forecast-sign-symbol text-3xl">{forecast.signSymbol}</span>
              )}
              <h3 className="text-xl font-bold text-[var(--color-text)]">
                {forecast.signName}
              </h3>
            </div>
            <h4 className="text-lg font-semibold text-[var(--color-primary)] mb-3">
              {forecast.title}
            </h4>
            <div
              className="text-[var(--color-text)] leading-relaxed prose prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: forecast.bodyHtml }}
            />
          </article>
        ))}
      </div>
    </div>
  );
}
